// App-level mount for the referral upsell dialog.
//
// Mounted once in App.tsx rather than per page: `/` sits outside PublicPageLayout
// while /dashboard, /faq and /services/* sit inside it, so no single layout covers
// the target surfaces. The path allowlist lives in constants/referralUpsell.ts.
//
// Renders nothing until the gate opens, so it costs one hook per navigation.

import * as Sentry from "@sentry/react";
import { lazy, Suspense } from "react";
import { useReferralUpsellPrompt } from "../../hooks/useReferralUpsellPrompt";
import { isLazyModule } from "../../utils/chunkReload";

// Lazy so the dialog and its confetti Lottie stay out of the main bundle — this
// component is imported eagerly by App.tsx, but the dialog is rare and optional.
//
// Both failure modes are handled, and they are genuinely different. A rejected import
// hits `.catch()`. But when the app's `vite:preloadError` handling cancels the event,
// Vite stops rethrowing and the promise **resolves with `undefined`** — which `.catch()`
// cannot see. That value reaches React.lazy, which evaluates `"default" in undefined`
// and throws from inside the render. A `.catch()` alone let a decorative dialog take
// /dashboard down to the root error boundary in production.
const EmptyReferralUpsellDialog = () => null;
const referralUpsellFallback = {
  default: EmptyReferralUpsellDialog,
} as unknown as typeof import("./ReferralUpsellDialog");

const ReferralUpsellDialog = lazy(() =>
  import("./ReferralUpsellDialog")
    .then((module) => (isLazyModule(module) ? module : referralUpsellFallback))
    .catch(() => referralUpsellFallback),
);

export default function ReferralUpsellPrompt() {
  const { open, dismiss, accept } = useReferralUpsellPrompt();

  if (!open) return null;

  return (
    // Silent boundary, deliberately not FeatureErrorBoundary: this renders invisibly on
    // every public page, so its fallback has to be nothing at all rather than that
    // component's "Something went wrong" card floating above the page. Without a
    // boundary of its own, anything thrown in here reaches the root Sentry boundary and
    // replaces the whole page — which is exactly what happened on /dashboard. Errors are
    // still reported, tagged so they are not read as a page-level failure.
    <Sentry.ErrorBoundary
      fallback={<></>}
      beforeCapture={(scope) => {
        scope.setTag("feature", "referral-upsell");
        scope.setLevel("warning");
      }}
    >
      <Suspense fallback={null}>
        <ReferralUpsellDialog open={open} onDismiss={dismiss} onAccept={accept} />
      </Suspense>
    </Sentry.ErrorBoundary>
  );
}
